import React, { useEffect, useState, useCallback } from "react";
import { useSearchParams } from "react-router-dom";
import {
    Table,
    Form,
    Container,
    Row,
    Col,
    InputGroup,
    Button,
    Spinner,
} from "react-bootstrap";
import { getAuthToken } from "../../utils/auth";
import PaginationBar from "../common/PaginationBar";
import { ITEMS_PER_PAGE } from "../../constants/appConstants";

const ActivationCodesManagement = () => {


    const [searchParams] = useSearchParams();
    const searchQuery = searchParams.get("search") || "";

    const baseUrl = process.env.REACT_APP_API_URL;
    const token = getAuthToken();


    const [activationCodes, setActivationCodes] = useState([]);
    const [search, setSearch] = useState(searchQuery);
    const [currentPage, setCurrentPage] = useState(0); // backend is 0-indexed
    const [totalPages, setTotalPages] = useState(0);
    const [loading, setLoading] = useState(false);

    const itemsPerPage = ITEMS_PER_PAGE;

    const fetchActivationCodes = useCallback(async () => {
        try {
            setLoading(true);

            const url = new URL(`${baseUrl}/admin/activation-codes`);
            url.searchParams.append("page", currentPage);
            url.searchParams.append("size", itemsPerPage);
            if (search.trim()) url.searchParams.append("query", search.trim());

            const res = await fetch(url, {
                method: "GET",
                headers: {
                    Authorization: `Bearer ${token}`,
                    "Content-Type": "application/json",
                },
            });

            if (!res.ok) {
                console.error("Failed to fetch users:", res.statusText);
                return;
            }

            const result = await res.json();
            if (result.status) {
                setActivationCodes(result.data || []);
                setTotalPages(result.pagination?.totalPages || 0);
            } else {
                setActivationCodes([]);
            }
        } catch (err) {
            console.error("Error fetching users:", err);
        } finally {
            setLoading(false);
        }
    }, [baseUrl, token, itemsPerPage, currentPage, search]); // include both currentPage and search

    useEffect(() => {
        fetchActivationCodes();
    }, [fetchActivationCodes]);

    useEffect(() => {
        setCurrentPage(0);
    }, [search]);

    const handleSearch = () => {
        setCurrentPage(0); // reset to first page when searching
    };

    const handleClear = () => {
        setSearch("");
        setCurrentPage(0);
    };

    return (
        <Container className="mt-4">
            <Row className="mb-3 align-items-center">
                <Col>
                    <h3>Activation Codes</h3>
                </Col>

                <Col xs="5">
                    <InputGroup>
                        <Form.Control
                            type="text"
                            placeholder="Search query ..."
                            value={search}
                            onChange={(e) => setSearch(e.target.value)}
                            onKeyDown={(e) => e.key === "Enter" && handleSearch()}
                        />
                        <Button variant="primary" onClick={handleSearch}>
                            Search
                        </Button>
                        <Button variant="outline-secondary" onClick={handleClear}>
                            Clear
                        </Button>
                    </InputGroup>
                </Col>
            </Row>

            {loading ? (
                <div className="text-center py-4">
                    <Spinner animation="border" />
                </div>
            ) : (
                <>
                    <Table striped bordered hover responsive>
                        <thead>
                            <tr>
                                <th>#</th> {/* Counter column */}
                                <th>Code</th>
                                <th>Type</th>
                                <th>Expire Date</th>
                                <th>Activation Date</th>
                                <th>Activated By</th>
                                <th>Sale agent</th>

                            </tr>
                        </thead>

                        <tbody>
                            {activationCodes.length > 0 ? (
                                activationCodes.map((s) => (
                                    <tr key={s.id}>
                                        <td>{s.id}</td>
                                        <td>{s.code}</td>
                                        <td>{s.type}</td>
                                        <td>{s.expiresAt}</td>
                                        <td>{s.usedAt}</td>
                                        <td>{s.userUsed}</td>
                                        <td>{s.agentName}</td>

                                    </tr>
                                ))
                            ) : (
                                <tr>
                                    <td colSpan="6" className="text-center text-muted">
                                        No users found
                                    </td>
                                </tr>
                            )}
                        </tbody>
                    </Table>

                    <PaginationBar
                        currentPage={currentPage}
                        totalPages={totalPages}
                        onPageChange={setCurrentPage}
                    />
                </>
            )}
        </Container>
    );
};

export default ActivationCodesManagement;
